import React, { PropsWithChildren, useContext } from 'react';

import { AddContextualData } from '@opendoor/observability';

import { LoadingPage } from 'platform/components/LoadingPage';
import {
  getAmplitudeResult,
  useGetSeveralAmplitudeExperiments,
  useGetSeveralAmplitudeFeatureFlags,
} from 'platform/graphql/useAmplitude';

import { AMPLITUDE_CONTEXT_EXPERIMENTS } from 'shared/declarations/AmplitudeExperiments';
import {
  AMPLITUDE_CONTEXT_FFS,
  AMPLITUDE_CONTEXT_FLAGS_LIST,
  ENUM_FLAG_VALUES,
} from 'shared/declarations/AmplitudeFeatureFlags';

export interface Assertions {
  isClosingDashboardNovoEnabled: boolean;
  isSlotBasedHomeAssessmentSchedulingEnabled: boolean;
  isAssessmentStepsSOTEnabled: boolean;
  isFinalOfferNovoEnabled: boolean;
}

export interface IExperimentationContext {
  loading: boolean;
  assertions: Assertions;
  enabledExperimentVariations?: Array<string | Record<string, string>>;
  enabledFeatureFlags?: Array<string | Record<string, string>>;
}

export const defaultExperimentationContext: IExperimentationContext = {
  loading: false,
  assertions: {
    isClosingDashboardNovoEnabled: false,
    isSlotBasedHomeAssessmentSchedulingEnabled: false,
    isAssessmentStepsSOTEnabled: false,
    isFinalOfferNovoEnabled: false,
  },
  enabledExperimentVariations: [],
  enabledFeatureFlags: [],
};

export const ExperimentationContext = React.createContext<IExperimentationContext>(
  defaultExperimentationContext,
);

/**
 * Hook used for getting experimentation data
 *
 * @throws error if hook is used outside of the scope of ExperimentationContext
 * @returns ExperimentationContext data
 */
export const useExperimentationContext = () => {
  const context = useContext(ExperimentationContext);

  if (context === undefined) {
    throw new Error(`useExperimentationContext must be used within a ExperimentationContext`);
  }
  return context;
};

/**
 * Context for amplitude data
 * @throws Error when data is not in the correct state
 */
export function ExperimentationContextProvider({ children }: { children: React.ReactNode }) {
  const amplitudeFeatureFlags = useGetSeveralAmplitudeFeatureFlags(AMPLITUDE_CONTEXT_FFS);
  // Merges Amplitude Feature Flags/Experiments, we will remove this once Optimizely is deprecated
  const features = getMergedVariants(amplitudeFeatureFlags.variants);
  const amplitudeExperiments = useGetSeveralAmplitudeExperiments(AMPLITUDE_CONTEXT_EXPERIMENTS);
  const experiments = getMergedVariants(amplitudeExperiments.variants);

  const assertions: Assertions = {
    isClosingDashboardNovoEnabled:
      getAmplitudeResult(AMPLITUDE_CONTEXT_FLAGS_LIST.closingDashboardNovo, features) ===
      ENUM_FLAG_VALUES.ON,
    isSlotBasedHomeAssessmentSchedulingEnabled:
      getAmplitudeResult(
        AMPLITUDE_CONTEXT_FLAGS_LIST.homeAssessmentSlotBasedScheduling,
        features,
      ) === ENUM_FLAG_VALUES.ON,
    isAssessmentStepsSOTEnabled:
      getAmplitudeResult(AMPLITUDE_CONTEXT_FLAGS_LIST.assessmentStepsSot, features) ===
      ENUM_FLAG_VALUES.ON,
    isFinalOfferNovoEnabled:
      getAmplitudeResult(AMPLITUDE_CONTEXT_FLAGS_LIST.finalOfferNovo, features) ===
      ENUM_FLAG_VALUES.ON,
  };
  const loading = amplitudeFeatureFlags.loading || amplitudeExperiments.loading;

  const value: IExperimentationContext = {
    loading,
    assertions,
    enabledExperimentVariations: experiments,
    enabledFeatureFlags: features,
  };

  const contextualData = {
    enabledExperimentVariations: experiments,
    enabledFeatureFlags: features,
    ...assertions,
  };

  if (loading) {
    return <LoadingPage from="ExperimentationContextProvider" reason="loading" />;
  }

  return (
    <ExperimentationContext.Provider value={value}>
      <AddContextualData data={contextualData}>{children}</AddContextualData>
    </ExperimentationContext.Provider>
  );
}

export const MockExperimentationContext: React.FC<
  PropsWithChildren & Partial<IExperimentationContext>
> = ({ children, ...props }) => (
  <ExperimentationContext.Provider value={{ ...defaultExperimentationContext, ...props }}>
    {children}
  </ExperimentationContext.Provider>
);

/**
 * Merges amplitude variants
 */
export const getMergedVariants = (
  amplitude: Record<string, string>[] = [],
): Array<string | Record<string, string>> => {
  const set = new Set<string | Record<string, string>>();
  const amplitudeVariantName = new Set<string>();
  amplitude.forEach((variant) => {
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set#using_the_set_object
    // Sets only check if a object strictly equals another object (if both objects point to the same memory location)
    // But, we want to check if the objects have the same data so we stringify the object and check if the stringified object is in the amplitudeVariantName set
    const variantName = JSON.stringify(variant);
    if (!amplitudeVariantName.has(variantName)) {
      set.add(variant);
      amplitudeVariantName.add(variantName);
    }
  });
  const variant = Array.from(set.values());
  return variant;
};
